/**
 * ReCharge Subscription Price Sync for Add to Cart Button
 * 
 * This script listens for ReCharge subscription price changes and updates
 * the price displayed in the "Add to Bag" CTA button on product pages.
 */

class ReChargePriceSync {
  constructor() {
    this.priceSelectors = {
      addToCartPrice: '.add-to-cart-price',
      rechargePrice: '.rc_widget__option__price, .rc-selling-plans__price, [data-rc-price]',
      rechargeWidget: '.rc_widget, .rc-widget, [data-recharge-widget]',
      productPrice: '.product-price__current, [data-product-price]',
    };
    
    this.observers = [];
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Initial setup with a slight delay to allow ReCharge to load
    setTimeout(() => {
      this.observeReChargeChanges();
      this.listenForVariantChanges();
      this.updatePriceFromReCharge();
    }, 500);
  }

  /**
   * Observe DOM changes for ReCharge widget updates
   */
  observeReChargeChanges() {
    const rechargeWidget = document.querySelector(this.priceSelectors.rechargeWidget);
    
    if (rechargeWidget) {
      const observer = new MutationObserver((mutations) => {
        // Check if the mutation affected price elements
        const hasPriceChange = mutations.some(mutation => {
          return Array.from(mutation.addedNodes).some(node => {
            return node.nodeType === 1 && (
              node.matches?.(this.priceSelectors.rechargePrice) ||
              node.querySelector?.(this.priceSelectors.rechargePrice)
            );
          });
        });

        if (hasPriceChange || mutations.some(m => m.type === 'characterData')) {
          this.updatePriceFromReCharge();
        }
      });

      observer.observe(rechargeWidget, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      this.observers.push(observer);
    }

    // Also observe the product form for ReCharge injections
    const productForm = document.querySelector('product-form-component, [data-type="add-to-cart-form"]');
    if (productForm) {
      const formObserver = new MutationObserver(() => {
        if (document.querySelector(this.priceSelectors.rechargeWidget)) {
          this.updatePriceFromReCharge();
        }
      });

      formObserver.observe(productForm, {
        childList: true,
        subtree: true,
      });

      this.observers.push(formObserver);
    }
  }

  /**
   * Listen for variant change events
   */
  listenForVariantChanges() {
    // Listen for Shopify variant changes
    document.addEventListener('variant:change', () => {
      setTimeout(() => this.updatePriceFromReCharge(), 300);
    });

    // Listen for custom variant update events
    document.addEventListener('variantUpdate', () => {
      setTimeout(() => this.updatePriceFromReCharge(), 300);
    });

    // Listen for ReCharge specific events
    document.addEventListener('recharge:sellingPlanChange', () => {
      setTimeout(() => this.updatePriceFromReCharge(), 100);
    });

    // Listen for input changes on ReCharge radio buttons/selects
    document.addEventListener('change', (e) => {
      if (e.target.matches('input[name*="selling_plan"], input[name*="purchase_option"], select[name*="selling_plan"]')) {
        setTimeout(() => this.updatePriceFromReCharge(), 100);
      }
    });
  }

  /**
   * Extract price from ReCharge widget or subscription selector
   */
  getReChargePrice() {
    // Check if a subscription option is selected
    const subscriptionInput = document.querySelector(
      'input[name*="selling_plan"]:checked, input[name*="purchase_option"][value*="subscription"]:checked'
    );

    if (!subscriptionInput) {
      return null; // No subscription selected
    }

    // Try to find the ReCharge price element
    const rechargePriceElements = document.querySelectorAll(this.priceSelectors.rechargePrice);
    
    for (const priceElement of rechargePriceElements) {
      // Check if this price element is visible and contains a price
      if (this.isVisible(priceElement)) {
        const priceText = priceElement.textContent.trim();
        const priceMatch = priceText.match(/[\$£€¥]\s*[\d,]+\.?\d*/);
        
        if (priceMatch) {
          return priceMatch[0];
        }
      }
    }

    // Alternative: Look for price in the selected option's parent
    if (subscriptionInput) {
      const parentLabel = subscriptionInput.closest('label, .rc_widget__option, .rc-selling-plan');
      if (parentLabel) {
        const priceText = parentLabel.textContent;
        const priceMatch = priceText.match(/[\$£€¥]\s*[\d,]+\.?\d*/);
        
        if (priceMatch) {
          return priceMatch[0];
        }
      }
    }

    return null;
  }

  /**
   * Check if an element is visible
   */
  isVisible(element) {
    if (!element) return false;
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && 
           style.visibility !== 'hidden' && 
           style.opacity !== '0';
  }

  /**
   * Get the original product price from the variant
   */
  getOriginalPrice() {
    const priceElement = document.querySelector(this.priceSelectors.productPrice);
    if (priceElement) {
      return priceElement.textContent.trim();
    }
    return null;
  }

  /**
   * Update the Add to Cart button price
   */
  updatePriceFromReCharge() {
    const addToCartPriceElements = document.querySelectorAll(this.priceSelectors.addToCartPrice);
    
    if (addToCartPriceElements.length === 0) {
      return; // No price display on button
    }

    // Get ReCharge price if subscription is selected
    const rechargePrice = this.getReChargePrice();
    
    // Decide which price to show
    const priceToShow = rechargePrice || this.getOriginalPrice();

    if (priceToShow) {
      addToCartPriceElements.forEach(element => {
        if (element.textContent.trim() !== priceToShow) {
          // Add a data attribute to track that we've synced this
          element.setAttribute('data-recharge-synced', 'true');
          element.textContent = priceToShow;
          
          // Trigger a subtle animation to show the price changed
          element.classList.add('price-updated');
          setTimeout(() => element.classList.remove('price-updated'), 300);
        }
      });
    }
  }

  /**
   * Cleanup observers when done
   */
  destroy() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
  }
}

// Initialize the sync when the script loads
if (typeof window !== 'undefined') {
  window.ReChargePriceSync = new ReChargePriceSync();
}

// Optional: Add subtle animation CSS
const style = document.createElement('style');
style.textContent = `
  .add-to-cart-price.price-updated {
    animation: priceHighlight 0.3s ease;
  }
  
  @keyframes priceHighlight {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
`;
document.head.appendChild(style);
